import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ChaloButton from "../../../../shared/widgets/chaloButton";
import ChaloTopNav from "../../../../shared/widgets/chaloTopNav";
import ChaloInputField from "../../../../shared/widgets/chaloInputField";
import ChaloDropDown from "../../../../shared/widgets/chaloDropDown";
import "./cameraOnBoarding.css";
import {
  validateCameraDetails,
  fetchCameraPositionData,
  fetchCameraServiceNames,
  fetchCameraBrands,
} from "../../../../services/vehicleMasterService";
import ChaloLoader from "../../../../shared/widgets/chaloLoader";
import { AuthenticationService } from "../../../../services/authorization";
import { validationRegex } from "../../../../Constant";

const CameraDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const feedData = location?.state || {};

  const [isLoading, setIsLoading] = useState(false);
  const authService = AuthenticationService.getInstance();
  const parentRoute = authService.getMasterParentRoute();

  const [formState, setFormState] = useState({
    busNumber: "",
    position: "",
    cameraType: "Trueview webcast RTC ( RTMP )",
    serviceName: "Live Feed",
    cameraBrand: "TrueView",
    ipAddress: "",
  });

  const [isFormValid, setIsFormValid] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!feedData?.cameraId || !feedData?.cameraBrand) {
      navigate(
        `${parentRoute}/scanCameraQr`
      );
      return;
    }

    if (feedData?.cameraBrand) {
      setFormState((prev) => ({
        ...prev,
        cameraBrand: feedData.cameraBrand,
      }));
    }
  }, [feedData, navigate, authService]);


  const { ipv4: ipv4Regex, ipv6: ipv6Regex } = validationRegex;

  useEffect(() => {
    const isPrama = formState.cameraBrand === 'Prama';
    const ipValid = !isPrama || (
      formState.ipAddress.trim() !== "" &&
      (ipv4Regex.test(formState.ipAddress.trim()) || ipv6Regex.test(formState.ipAddress.trim()))
    );
    setIsFormValid(
      formState.busNumber.trim() !== "" &&
        formState.position.trim() !== "" &&
        formState.cameraType.trim() !== "" &&
        formState.serviceName.trim() !== "" &&
        formState.cameraBrand.trim() !== "" &&
        ipValid
    );
    setFieldErrors({});
  }, [formState]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const [positionOptions, setPositionOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [cameraBrand, setCameraBrand] = useState([]);

  const handleNavigation = useCallback(async () => {
    setIsLoading(true);
    const payload = {
      cameraId: feedData.cameraId,
      cameraBrand: formState.cameraBrand,
      cameraType: formState.cameraType,
      vehicleNo: formState.busNumber,
      position: formState.position,
      serviceName: formState.serviceName,
      ...(formState.cameraBrand === 'Prama' && { ipAddress: formState.ipAddress.trim() }),
    };

    try {
      const response = await validateCameraDetails(JSON.stringify(payload));
      if (response.status === "SUCCESS") {
        setIsLoading(false);
        navigate(
          `${parentRoute}/cameraConfirmDetails`,
          { state: { ...feedData, ...payload } }
        );
      } else if (response.status === "FAILURE") {
        setIsLoading(false);
        setFieldErrors(response.error);
      }
    } catch (error) {
      setIsLoading(false);
      if (error?.status === "FAILURE" && error.error) {
        setFieldErrors(error.error);
      } else {
        setFieldErrors({
          general: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }, [navigate, feedData, formState, authService]);

  useEffect(() => {
    const getDropdownData = async () => {
      try {
        const [positions, services, brands] = await Promise.all([
          fetchCameraPositionData(),
          fetchCameraServiceNames(),
          fetchCameraBrands(),
        ]);
        setPositionOptions(positions.map((item) => ({ label: item, value: item })));
        setServiceOptions(services.map((item) => ({ label: item, value: item })));
        setCameraBrand(brands.map((item) => ({ label: item, value: item })));
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    };

    getDropdownData();
  }, []);

  const cameraType = useMemo(
    () => [
      {
        label: "Trueview webcast RTC ( RTMP )",
        value: "Trueview webcast RTC ( RTMP )",
      },
    ],
    []
  );

  return (
    <div className="camera-wrapper row">
      {isLoading && <ChaloLoader />}
      <div className="camera-details-container">
        <ChaloTopNav navContent="Required information" />
        <div className="col-md-12 camera-detail-content">
          <div className="passed-message-container">
            <h4>Camera Status</h4>
            <p className={feedData.cameraConnected ? "Passed" : "failed"}>
              {feedData.cameraConnected ? "Connected" : "not Connected"}
            </p>
          </div>
          <div className="passed-message-container">
            <h4>RTMP live feed</h4>
            <p className={feedData.liveFeed ? "Passed" : "failed"}>
              {feedData.liveFeed ? "Active" : "InActive"}
            </p>
          </div>
          <div className="passed-message-container">
            <h4>SD card check</h4>
            <p
              className={
                feedData.sdCardInfo?.sdCardPresent ? "Passed" : "failed"
              }
            >
              {feedData.sdCardInfo?.sdCardPresent ? "Passed" : "Not Available"}
            </p>
          </div>

          <div className="camera-detail-form">
            <h4>Camera details</h4>

            <div className="chalo-input-field">
              <ChaloInputField
                label="Camera id"
                value={feedData.cameraId || ""}
                placeholder="Camera id"
                disabled
                id="cameraId"
              />
            </div>

            <div className="chalo-input-field">
              <ChaloInputField
                label="Bus number"
                placeholder="Bus number *"
                name="busNumber"
                value={formState.busNumber}
                onChange={handleChange}
                id="busNumberId"
                error={fieldErrors.vehicleNo}
              />
              {fieldErrors.vehicleNo && (
                <div className="camera-error-text">{fieldErrors.vehicleNo}</div>
              )}
            </div>

            <div className="chalo-input-field">
              <ChaloDropDown
                label="Position"
                options={positionOptions}
                value={formState.position}
                name="position"
                placeholder="Position *"
                onChange={handleChange}
                id="position-id-unique"
                error={fieldErrors.position}
              />
              {fieldErrors.position && (
                <div className="camera-error-text">{fieldErrors.position}</div>
              )}
            </div>

            <div className="chalo-input-field">
              <ChaloDropDown
                label="Service name"
                options={serviceOptions}
                value={formState.serviceName}
                name="serviceName"
                placeholder="Service name *"
                onChange={handleChange}
                id="serviceName"
                disabled
                error={fieldErrors.serviceName}
              />
              {fieldErrors.serviceName && (
                <div className="camera-error-text">{fieldErrors.serviceName}</div>
              )}
            </div>

            <div className="chalo-input-field">
              <ChaloDropDown
                label="Camera brand"
                options={cameraBrand}
                value={formState.cameraBrand}
                name="cameraBrand"
                placeholder="Camera Brand *"
                onChange={handleChange}
                id="camera-brand-id-unique"
                disabled
              />
            </div>

            {formState.cameraBrand === 'Prama' && (
              <div className="chalo-input-field">
                <ChaloInputField
                  label="Camera IP Address"
                  placeholder="Camera IP Address (IPv4 or IPv6) *"
                  name="ipAddress"
                  value={formState.ipAddress}
                  onChange={handleChange}
                  id="ipAddress"
                />
                {formState.ipAddress.trim() !== "" &&
                  !ipv4Regex.test(formState.ipAddress.trim()) &&
                  !ipv6Regex.test(formState.ipAddress.trim()) && (
                  <div className="camera-error-text">Enter a valid IPv4 or IPv6 address</div>
                )}
              </div>
            )}

            <div className="chalo-input-field">
              <ChaloDropDown
                label="Camera type"
                options={cameraType}
                value={formState.cameraType}
                name="cameraType"
                placeholder="Camera type *"
                onChange={handleChange}
                id="cameraType"
                disabled
              />
            </div>
          </div>
        </div>

        <div className="col-md-12 live-feed-footer">
          <ChaloButton
            buttonText="Submit"
            onClick={handleNavigation}
            disabled={!isFormValid}
          />
        </div>
      </div>
    </div>
  );
};

export default CameraDetails;
